import React, { useCallback, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import mixpanel from 'mixpanel-browser';
import httpClient from '@sunbit-dev/common/services/httpClient';
import { initConfig } from '@sunbit-dev/common/services/config';
import { getUrlParameter } from '@sunbit-dev/common/helpers/url';
import { SignIn } from '@sunbit-dev/web-ui-lib';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../contexts/appContext';
import { LoaderActionContext } from '../contexts/loaderContext';
import SunbitApi from '../services/SunbitApi';
import { signInPopupType } from '../constants/models';
import { CompanyUrls } from '../constants/appConstants';

declare const window: Window & { sunbitEnv: string };

interface IProps {
  login: (userName: string, userToken: string) => void;
}

export const LoginContainer = ({ login }: IProps) => {
  const { setLoader } = React.useContext(LoaderActionContext);
  const appContext = React.useContext(AppContext);
  const { setCredentials, setSignInPopup, onFieldChange, signIn, setResetPasswordPopupStatus, credentials } =
    appContext;
  const [isUserSignedUp, setIsUserSignedUp] = React.useState(true);
  const history = useHistory();

  const handleLogin = async (userName: string, password: string) => {
    try {
      setLoader(true);

      const loginResponse = await SunbitApi.login(userName, password, {
        eventName: 'Login Request',
        props: { userName },
      });

      if (loginResponse.redirect_url) {
        window.location.href = loginResponse.redirect_url;
        return;
      }
      const newToken = loginResponse.access_token;

      login(userName, newToken);

      history.push('/');
    } catch (error) {
      if ((error as any).message?.includes('422')) {
        datadogRum.addUserAction('errorStartup-Error-unauthorized', appContext);
        setSignInPopup(signInPopupType.signInFail);
      } else if ((error as any).message?.includes('423')) {
        datadogRum.addUserAction('errorStartup-Error-accountLocked', appContext);
        setSignInPopup(signInPopupType.signInLocked);
      } else {
        datadogRum.addUserAction('errorStartup-fallbackError', appContext);
      }
    } finally {
      setLoader(false);
    }
  };

  const handleRegistration = async (newPassword: string) => {
    const { userToken, userName } = appContext.credentials || {};

    try {
      setLoader(true);
      const registerResponse = await SunbitApi.register(userName, newPassword, userToken);

      mixpanel.track('Registration', { userName });

      login(userName, registerResponse.access_token);

      history.push('/');
      setLoader(false);
    } catch (error) {
      setLoader(false);
      if ((error as any).message?.includes('403')) {
        datadogRum.addUserAction('errorStartup-Error-unauthorized', appContext);
      } else {
        datadogRum.addUserAction('errorStartup-fallbackError', appContext);
      }
    }
  };

  const resetPassword = async () => {
    try {
      setLoader(true);
      await SunbitApi.resetPassword(appContext.resetPassword.resetPasswordUser.value);
      appContext.setResetPasswordPopupStatus();
      setLoader(false);
      appContext.setSignInPopup(signInPopupType.resetPasswordSuccess);
      mixpanel.track('Reset Password', { userName: appContext.resetPassword.resetPasswordUser.value });
    } catch (error) {
      setLoader(false);
      appContext.setResetPasswordPopupStatus();
      if ((error as any).message?.includes('423')) {
        appContext.setSignInPopup(signInPopupType.resetPasswordFailedLocked);
        datadogRum.addUserAction('errorResetPassword-Error-Locked', appContext);
      } else {
        appContext.setSignInPopup(signInPopupType.resetPasswordFailedGeneral);
        datadogRum.addUserAction('errorResetPassword-fallbackError', appContext);
      }
    }
  };

  useEffect(() => {
    const initialUserName = decodeURIComponent((getUrlParameter('email') as string) || '');

    const loadConfig = async () => {
      SunbitApi.init(httpClient);
      const configService = await initConfig(window.sunbitEnv, '/config');
      httpClient.setBaseUrl(configService.getItem('apiUrl'));
      const userToken = decodeURIComponent((getUrlParameter('token') as string) || '');

      if (initialUserName) {
        setIsUserSignedUp(false);
      }

      setCredentials({ userName: initialUserName || '', userToken, error: '' });
    };

    loadConfig();

    if (initialUserName) {
      mixpanel.track('Sign Up Page', { userName: initialUserName });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = useCallback(
    (e) => {
      if (e.fieldName === 'password' && isUserSignedUp) {
        e.passValidation = true;
      }

      onFieldChange(e);
    },
    [isUserSignedUp, onFieldChange]
  );

  return (
    <SignIn
      {...{ onChange, setResetPasswordPopupStatus, resetPassword, setSignInPopup }}
      privacyPolicyUrl={CompanyUrls.privacyPolicyUrl}
      termsOfUseUrl={CompanyUrls.termsOfUseUrl}
      ratesAndTermsUrl={CompanyUrls.ratesAndTermsUrl}
      username={isUserSignedUp ? signIn.userName : credentials.userName}
      password={signIn.password}
      confirmPassword={signIn.confirmPassword}
      isSignedUp={isUserSignedUp}
      handleLogin={handleLogin}
      handleRegistration={handleRegistration}
      setResetPasswordPopupStatus={setResetPasswordPopupStatus}
      resetPasswordPopup={appContext.resetPassword.resetPasswordPopupStatus}
      resetPopupField={appContext.resetPassword.resetPasswordUser}
      signInPopupData={signIn.signInPopup}
    />
  );
};
